import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, GraduationCap, Users, Target, BookOpen, ChevronRight, Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FormacionPage = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    mensaje: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${API}/contacto`, {
        ...formData,
        mensaje: `[INTERÉS EN FORMACIÓN] ${formData.mensaje}`
      });
      
      if (response.data.success) {
        setStatus({
          type: 'success',
          message: 'Hemos recibido tu interés. Te contactaremos pronto con más información sobre nuestras formaciones.'
        });
        setFormData({ nombre: '', telefono: '', mensaje: '' });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Ha ocurrido un error. Por favor, llámanos directamente al 858 130 213.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const caracteristicas = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Para Todos los Niveles",
      description: "Desde iniciación para particulares hasta técnicas avanzadas para profesionales experimentados."
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Cursos Personalizados",
      description: "Adaptamos el contenido y la duración a tus necesidades específicas y objetivos."
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Formación Práctica",
      description: "Aprende haciendo. Nuestros cursos combinan teoría con práctica real en taller."
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: "+30 Años de Experiencia",
      description: "Formadores con décadas de experiencia en el sector de yesos y pladur."
    }
  ];

  return (
    <div className="industrial-bg" data-testid="formacion-page">
      {/* Hero */}
      <section className="bg-[#1A365D] py-16">
        <div className="section-container">
          <nav className="text-sm text-gray-400 mb-4" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2">
              <li><Link to="/" className="hover:text-white">Inicio</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li className="text-white">Formación</li>
            </ol>
          </nav>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase mb-4">
            Formación en Pladur y Yesos
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            Próximamente cursos de formación para particulares y profesionales. 
            Más de 30 años de experiencia a tu servicio.
          </p>
        </div>
      </section>

      {/* Introducción */}
      <section className="py-20">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#3182CE]/10 text-[#3182CE] rounded-sm mb-8">
              <GraduationCap className="w-10 h-10" />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A365D] uppercase mb-6">
              Nuestra Experiencia, Tu Formación
            </h2>
            <div className="prose prose-lg text-[#4A5568] mx-auto">
              <p>
                En <strong>Fornes e Hijos</strong> contamos con más de <strong>30 años de experiencia</strong> en 
                el sector de yesos, escayolas y sistemas de pladur. Durante todo este tiempo hemos trabajado 
                junto a los mejores profesionales de la construcción, aprendiendo y perfeccionando las técnicas 
                que hacen de este oficio un arte.
              </p>
              <p>
                Ahora queremos compartir ese conocimiento contigo. <strong>Próximamente comenzaremos a ofrecer 
                cursos de formación</strong> tanto para particulares que quieran aprender a realizar sus propias 
                reformas, como para profesionales que deseen actualizar sus conocimientos o especializarse en 
                nuevas técnicas.
              </p>
              <p>
                Nuestros cursos se <strong>adaptarán a tu nivel y necesidades</strong>. Desde lo más básico 
                hasta técnicas avanzadas de instalación, aislamiento acústico y térmico, y acabados profesionales.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Características */}
      <section className="py-20 bg-white">
        <div className="section-container">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A365D] uppercase text-center mb-12">
            ¿Qué Ofreceremos?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {caracteristicas.map((item, index) => (
              <div
                key={index}
                className="bg-[#F7FAFC] p-8 rounded-sm border border-gray-200 text-center card-hover"
                data-testid={`caracteristica-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#3182CE]/10 text-[#3182CE] rounded-sm mb-6">
                  {item.icon}
                </div>
                <h3 className="font-heading text-xl font-bold text-[#1A365D] mb-3">
                  {item.title}
                </h3>
                <p className="text-[#4A5568]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tipos de cursos */}
      <section className="py-20 industrial-bg">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Particulares */}
            <div className="bg-white p-8 rounded-sm border border-gray-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#3182CE]/10 rounded-sm flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#3182CE]" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#1A365D] uppercase">
                  Para Particulares
                </h3>
              </div>
              <p className="text-[#4A5568] mb-6">
                ¿Quieres aprender a realizar pequeñas reformas en tu hogar? Nuestros cursos de iniciación 
                te enseñarán las bases para trabajar con pladur y yesos de forma segura y profesional.
              </p>
              <ul className="space-y-3 text-[#4A5568]">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#3182CE] rounded-full"></div>
                  Introducción al pladur y herramientas básicas
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#3182CE] rounded-full"></div>
                  Montaje de tabiques sencillos
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#3182CE] rounded-full"></div>
                  Tratamiento de juntas y acabados
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#3182CE] rounded-full"></div>
                  Pequeñas reparaciones y parches
                </li>
              </ul>
            </div>

            {/* Profesionales */}
            <div className="bg-white p-8 rounded-sm border border-gray-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#1A365D]/10 rounded-sm flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-[#1A365D]" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#1A365D] uppercase">
                  Para Profesionales
                </h3>
              </div>
              <p className="text-[#4A5568] mb-6">
                Actualiza tus conocimientos o especialízate en nuevas técnicas. Cursos avanzados para 
                profesionales del sector que buscan ampliar sus competencias.
              </p>
              <ul className="space-y-3 text-[#4A5568]">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#1A365D] rounded-full"></div>
                  Sistemas de aislamiento acústico avanzado
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#1A365D] rounded-full"></div>
                  Techos técnicos y registrables
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#1A365D] rounded-full"></div>
                  Cumplimiento del CTE y normativa
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#1A365D] rounded-full"></div>
                  Nuevos productos y técnicas del mercado
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Formulario de interés */}
      <section className="py-20 bg-[#1A365D]" data-testid="formacion-interes-section">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Texto */}
            <div>
              <h2 className="font-heading text-3xl font-bold text-white uppercase mb-6">
                ¿Te Interesa?
              </h2>
              <p className="text-lg text-gray-300 mb-6">
                Si estás interesado en nuestras futuras formaciones, déjanos tus datos y te contactaremos 
                cuando tengamos fechas y programas definidos. Sin compromiso.
              </p>
              <p className="text-gray-300 mb-8">
                También puedes llamarnos directamente para más información:
              </p>
              <div className="space-y-4">
                <a
                  href="tel:858130213"
                  className="flex items-center gap-3 text-white hover:text-blue-200 transition-colors"
                  data-testid="formacion-phone"
                >
                  <Phone className="w-5 h-5" />
                  <span className="text-lg">858 130 213</span>
                </a>
                <a
                  href="mailto:fornesehijos@gmail.com?subject=Interés en formación"
                  className="flex items-center gap-3 text-white hover:text-blue-200 transition-colors"
                  data-testid="formacion-email"
                >
                  <Mail className="w-5 h-5" />
                  <span>fornesehijos@gmail.com</span>
                </a>
              </div>
            </div>

            {/* Formulario */}
            <div className="bg-white p-8 rounded-sm">
              <h3 className="font-heading text-xl font-bold text-[#1A365D] mb-6">
                Comunica tu Interés
              </h3>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="formacion-form">
                <div>
                  <label htmlFor="nombre" className="block text-sm font-medium text-[#2D3748] mb-2">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    minLength={2}
                    className="form-input"
                    placeholder="Tu nombre"
                    data-testid="formacion-input-nombre"
                  />
                </div>

                <div>
                  <label htmlFor="telefono" className="block text-sm font-medium text-[#2D3748] mb-2">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    minLength={9}
                    className="form-input"
                    placeholder="Tu teléfono"
                    data-testid="formacion-input-telefono"
                  />
                </div>

                <div>
                  <label htmlFor="mensaje" className="block text-sm font-medium text-[#2D3748] mb-2">
                    ¿Qué tipo de formación te interesa?
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    rows={3}
                    className="form-input resize-none"
                    placeholder="Particular, profesional, temas de interés..."
                    data-testid="formacion-input-mensaje"
                  />
                </div>

                {status.message && (
                  <div 
                    className={`flex items-center gap-3 p-4 rounded-sm ${
                      status.type === 'success' 
                        ? 'bg-green-50 text-green-800 border border-green-200' 
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                    data-testid="formacion-status-message"
                  >
                    {status.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    )}
                    <p className="text-sm">{status.message}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="formacion-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Quiero que me informen
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FormacionPage;
